import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { useController } from "react-hook-form";
import { Check } from "@/components/icons";
import { formatHour12, HOUR_TIME_OPTIONS } from "@/common/utils/date";
import { useFieldRef } from "@/hooks/useFieldRef";
import { cn } from "@/lib/utils";
import { DropdownScrollArea } from "./DropdownScrollArea";

export interface FormTimeSelectProps {
  /** react-hook-form 필드 이름 (`"HH:MM:SS"`) */
  name: string;
  placeholder: string;
  disabled?: boolean;
  /** 기본 32px, `md` 는 전체 화면 폼의 48px 입력과 같은 크기 */
  size?: "sm" | "md";
  /** 선택 가능한 시각. 기본은 1시간 단위 24개 */
  options?: { value: string; label: string }[];
}

/**
 * 1시간 단위 시각 선택. 라벨 없이 `FormRow` 의 children 으로 쓴다.
 *
 * Radix Select 가 아니라 Popover 인 이유: 전역 네이티브 스크롤바가 16px 을 가져가
 * 좁은 목록에서 항목이 잘린다. `DropdownScrollArea` 는 thumb 을 목록 위에 겹쳐 그린다.
 */
export function FormTimeSelect({
  name,
  placeholder,
  disabled = false,
  size = "sm",
  options = HOUR_TIME_OPTIONS,
}: FormTimeSelectProps) {
  const { field, fieldState } = useController({ name });
  const fieldRef = useFieldRef<HTMLButtonElement>(field);
  const [open, setOpen] = React.useState(false);
  const scrollerRef = React.useRef<HTMLDivElement | null>(null);
  const listRef = React.useRef<HTMLDivElement | null>(null);

  /**
   * 선택한 시각을 목록 한가운데로. Radix 기본 포커스는 첫 항목을 화면에 넣으려고
   * 스크롤을 되돌리므로 포커스와 스크롤을 직접 잡고, 여는 애니메이션(zoom-in-95)에
   * rect 가 축소돼 나오므로 offset 으로 잰다.
   */
  const centerSelected = (event: Event) => {
    event.preventDefault();
    const scroller = scrollerRef.current;
    const list = listRef.current;
    const selected = list?.querySelector<HTMLElement>('[data-selected="true"]');
    if (!scroller || !list) return;

    // 아직 고른 값이 없으면 첫 항목 — 포커스를 안 주면 키보드로 목록에 못 들어간다
    const target = selected ?? (list.firstElementChild as HTMLElement | null);
    target?.focus({ preventScroll: true });
    if (!selected) return;

    scroller.scrollTop =
      selected.offsetTop -
      list.offsetTop -
      (scroller.clientHeight - selected.offsetHeight) / 2;
  };

  const selectedLabel = formatHour12(field.value as string | null);

  return (
    <div className="min-w-0 flex-1">
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Trigger asChild>
          <button
            ref={fieldRef}
            type="button"
            name={name}
            disabled={disabled}
            className={cn(
              "typo-body-2-medium flex w-full items-center bg-static-white inset-border-solid-normal",
              size === "md" ? "h-12 rounded-xl px-4" : "h-8 rounded-sm px-2",
              selectedLabel ? "text-labels-primary" : "text-labels-assistive",
              fieldState.invalid && "inset-border-error",
              disabled && "cursor-not-allowed bg-fills-neutral",
            )}
          >
            <span className="truncate">{selectedLabel ?? placeholder}</span>
          </button>
        </PopoverPrimitive.Trigger>

        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            className={cn(
              "bg-static-white text-labels-primary",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
              "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
              "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
              "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2",
              "z-50 w-33 overflow-hidden rounded-xl",
              "inset-border-solid-normal shadow-[0px_4px_16px_0px_rgba(0,0,0,0.05)]",
            )}
            sideOffset={4}
            align="start"
            onOpenAutoFocus={centerSelected}
          >
            <DropdownScrollArea
              className="h-50"
              scrollerRef={scrollerRef}
              onWheel={(e) => e.stopPropagation()}
            >
              <div ref={listRef} className="flex flex-col gap-1 px-2 py-3">
                {options.map((option) => {
                  const isSelected = field.value === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      data-selected={isSelected}
                      onClick={() => {
                        field.onChange(option.value);
                        setOpen(false);
                      }}
                      className={cn(
                        "flex h-8 w-full cursor-pointer items-center justify-between gap-2 rounded-lg px-2 py-1",
                        "typo-label-1-semibold whitespace-nowrap text-labels-primary outline-none select-none",
                        "hover:bg-fills-primary focus:bg-fills-primary",
                      )}
                    >
                      {option.label}
                      {isSelected && (
                        <Check size={20} color="accent-mute-green" />
                      )}
                    </button>
                  );
                })}
              </div>
            </DropdownScrollArea>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    </div>
  );
}

FormTimeSelect.displayName = "FormTimeSelect";
